import { ApolloClient, ApolloProvider, InMemoryCache, from } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { createUploadLink } from 'apollo-upload-client';
import React from 'react';
import { createRoot } from 'react-dom/client';

import { getToken, logout } from 'utils/auth';
import paginationHelper, { auditPaginationHelper } from 'utils/pagination-helper';

import './index.scss';
import reportWebVitals from './reportWebVitals';
import App from './routes';
import './tailwind.css';

const httpLink = createUploadLink({
  uri: process.env.REACT_APP_INDIA_BACKEND,
  credentials: 'include',
});

const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors)
    graphQLErrors.forEach(({ extensions }) => {
      if (extensions && extensions.code === 'UNAUTHENTICATED') logout();
    });
  if (networkError) console.log(`[Network error]: ${networkError}`);
});

const authLink = setContext((_, { headers }) => {
  // get the authentication token from local storage if it exists
  // const token = localStorage.getItem('token');
  // return the headers to the context so httpLink can read them
  return {
    headers: {
      ...headers,
      authorization: getToken(),
      'Apollo-Require-Preflight': 'true',
    },
  };
});

const client = new ApolloClient({
  link: from([errorLink, authLink.concat(httpLink)]),
  connectToDevTools: process.env.NODE_ENV !== 'production',
  cache: new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          companies: paginationHelper(),
          deals: paginationHelper(),
          syndicates: paginationHelper(['filters']),
          users: paginationHelper(),
          investmentEntities: paginationHelper(),
          dematAccounts: paginationHelper(),
          posts: paginationHelper(['filters']),
          shareAssets: paginationHelper(['filters']),
          convertibleAssets: paginationHelper(['filters']),
          dealExits: paginationHelper(['filters']),
          wishlistCompanies: paginationHelper(['filters']),
          companyEventWebhooks: paginationHelper(['filters']),
          accreditationSubmissions: paginationHelper(),
        },
      },
      DealType: {
        fields: {
          closings: paginationHelper(),
          audits: auditPaginationHelper(),
          invites: paginationHelper(),
        },
      },
      SyndicateType: {
        fields: {
          deals: paginationHelper(),
          syndicateUsers: paginationHelper(),
        },
      },
      FundType: {
        fields: {
          investments: paginationHelper(),
          fundClosings: paginationHelper(['filters']),
        },
      },
    },
  }),
});

const container = document.getElementById('root');
const root = createRoot(container!);

root.render(
  <React.StrictMode>
    <ApolloProvider client={client}>
      <App />
    </ApolloProvider>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
